import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import {
  loadLastFilter,
  seedFromStored,
} from '../../components/questions/layout/questionsFilterStorage';
import { normalizeTaxonomy, toSearch } from '../../components/questions/layout/useQuestionFilters';
import { QuestionsLayout } from '../../components/questions/QuestionsLayout';
import { MathJaxProvider } from '../../components/rich-text';

// TanStack Router JSON-parses search values, so `?subjectId=4` arrives as a number.
const idParam = z.preprocess((v) => (typeof v === 'number' ? String(v) : v), z.string()).optional();

/**
 * Search-param schema for the /questions pool browser.
 *
 * All fields optional — an absent param means "no filter applied".
 * standardId is coerced from string (URL always strings) to number.
 */
export const questionsSearchSchema = z.object({
  curriculumId: idParam,
  versionId: idParam,
  classId: idParam,
  subjectId: idParam,
  chapterId: idParam,
  topicId: idParam,
  subtopicId: idParam,
  category: z.string().optional(),
  types: z.array(z.string()).optional(),
  hardness: z.string().optional(),
  importance: z.string().optional(),
  standard: z.string().optional(),
  standardId: z.coerce.number().optional(),
});

export type QuestionsSearch = z.infer<typeof questionsSearchSchema>;

export const Route = createFileRoute('/_admin/questions')({
  validateSearch: questionsSearchSchema,
  beforeLoad: ({ search }) => {
    const stored = loadLastFilter();
    const seed = seedFromStored(
      normalizeTaxonomy(search as QuestionsSearch),
      stored && normalizeTaxonomy(stored),
    );
    if (seed) {
      throw redirect({
        to: '/questions',
        search: toSearch(seed),
        replace: true,
      });
    }
  },
  component: QuestionsRoute,
});

function QuestionsRoute() {
  return (
    <MathJaxProvider>
      <QuestionsLayout />
    </MathJaxProvider>
  );
}
